/* global axios */
// DahReply — Credit Usage page (docs/credit_usage/ in singabot_fastapi).
//
// `credit_usage` lives in the FastAPI backend's own DB, not Chatwoot's, so
// this goes through the Rails reverse-proxy the same way Usage/Top-Up does
// (see store/modules/stripe.js's `knowledgeProxy`) rather than a Rails-native
// `ApiClient` resource like Audit Events.
//
// Page-number pagination (not "load more"): unlike audit_events, the backend
// list endpoint returns a real `total` count cheaply, so a numbered pager
// (PaginationBar) is the natural fit here.

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

const buildQuery = filters => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(v => params.append(key, v));
    } else {
      params.append(key, value);
    }
  });
  return params.toString();
};

const state = {
  records: [],
  page: 1,
  pageSize: 20,
  total: 0,
  summary: null,
  uiFlags: {
    fetchingList: false,
    fetchingSummary: false,
  },
};

const getters = {
  getRecords(_state) {
    return _state.records;
  },
  getPagination(_state) {
    return {
      currentPage: _state.page,
      pageSize: _state.pageSize,
      totalPages: Math.max(1, Math.ceil(_state.total / _state.pageSize)),
      total: _state.total,
    };
  },
  getSummary(_state) {
    return _state.summary;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

const actions = {
  async fetch({ commit, rootGetters }, filters = {}) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('setUIFlag', { fetchingList: true });
    try {
      const page = filters.page || 1;
      const pageSize = filters.page_size || 20;
      const query = buildQuery({ ...filters, page, page_size: pageSize });
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/account/${accountId}/credit-usage?${query}`)
      );
      commit('setList', data);
    } finally {
      commit('setUIFlag', { fetchingList: false });
    }
  },

  async fetchSummary({ commit, rootGetters }, filters = {}) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('setUIFlag', { fetchingSummary: true });
    try {
      const query = buildQuery(filters);
      const path = `/account/${accountId}/credit-usage/summary${
        query ? `?${query}` : ''
      }`;
      const { data } = await axios.get(knowledgeProxy(accountId, path));
      commit('setSummary', data);
    } finally {
      commit('setUIFlag', { fetchingSummary: false });
    }
  },
};

const mutations = {
  setList(_state, data) {
    _state.records = data.results;
    _state.page = data.page;
    _state.pageSize = data.page_size;
    _state.total = data.total;
  },
  setSummary(_state, summary) {
    _state.summary = summary;
  },
  setUIFlag(_state, flag) {
    _state.uiFlags = { ..._state.uiFlags, ...flag };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
